import { Navigate, useLocation } from 'react-router-dom';
import { RouteConfig } from '../types';
import * as Components from '../lazyComponents';

/**
 * GH#1803 — /register redirige al wizard /signup conservando la query.
 *
 * /register (RegisterForm) es un formulario distinto que descarta el ?plan que
 * el cliente eligió en /pricing y no tiene casilla de política de privacidad
 * (privacyAcceptedAt queda null, hueco de Ley 172-13, #1060). El alta canónica
 * es el wizard /signup, que lee el plan con resolvePlanFromURL y recoge el
 * consentimiento. Se preserva `search` para no perder ?plan/&addons/&cycle de
 * quien llega por un enlace o marcador viejo a /register.
 */
export const RegisterRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/signup${search}`} replace />;
};

export const authRoutes: RouteConfig[] = [
  {
    path: '/login',
    element: <Components.LoginPage />,
    requiresAuth: false,
    requiresLayout: false,
  },
  {
    path: '/register',
    element: <RegisterRedirect />,
    requiresAuth: false,
    requiresLayout: false,
  },
  {
    path: '/forgot-password',
    element: <Components.ForgotPasswordPage />,
    requiresAuth: false,
    requiresLayout: false,
  },
  {
    path: '/email-verification',
    element: <Components.EmailVerificationPage />,
    requiresAuth: true,
    requiresLayout: false,
  },
  /**
   * #1062 — el destino del enlace que sale en el correo de verificación.
   *
   * La ruta la fija el backend, no acá: la arma `buildVerificationLink()` en
   * `apps/api/src/modules/integrations/providers/email/handlers/email-verification-requested.handler.ts:160`
   * como `${APP_PUBLIC_URL}/verificar-email?token=…`. Si una de las dos se
   * mueve sola, el correo apunta a la nada — y la nada acá NO se ve como error:
   * el catch-all de la SPA devuelve 200 con el index para cualquier ruta.
   * Por eso hay una prueba que ata este literal al del handler.
   *
   * `requiresAuth: false` es la parte que importa y no es cosmética: quien hace
   * clic viene de su bandeja, sin sesión. Con el guard puesto lo mandaríamos a
   * /login y el token se perdería en el camino — la página existiría y aun así
   * nadie verificaría nunca.
   */
  {
    path: '/verificar-email',
    element: <Components.VerifyEmailPage />,
    requiresAuth: false,
    requiresLayout: false,
  },
  {
    path: '/onboarding',
    element: <Components.OnboardingPage />,
    requiresAuth: true,
    requiresLayout: false,
  },
  {
    path: '/admin/login',
    element: <Components.AdminLoginPage />,
    requiresAuth: false,
    requiresLayout: false,
  },
  {
    path: '/profile',
    element: <Components.ProfilePage />,
    roles: [
      'SUPER_ADMIN',
      'AGENCY_ADMIN',
      'PROJECT_DEVELOPER_ADMIN',
      'BROKER_MANAGER_ADMIN',
      'AGENCY_AGENT_USER',
      'LEGAL_MODULE',
      'ACCOUNTING_TEAM',
      'OPERATIONAL_TEAM',
      'CONDOMINIUM_ADMIN',
    ],
    requiresAuth: true,
    requiresLayout: true,
  },
];
